/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { EmojiContainer } from '@jira/polaris__lib-emoji-picker/src/useEmoji.tsx';
import { EntityLimitsContainer } from '@jira/polaris__component-entity-limits-store/src/EntityLimitsContainer.tsx';
import { NavigationCollectionsContainer } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';
import { OnboardingFlowsContainer } from '@jira/polaris__lib-onboarding-flows/src/controllers/index.tsx';
import type {
	NavigationCollectionsOnFailure,
	ActionWithSubject,
} from '@jira/polaris__component-navigation-collections-store/src/NavigationCollection.types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import {
	NavigationCollectionsRemoteProvider,
	useNavigationCollectionsRemote,
	NavigationViewRemoteProvider,
} from '../../../controllers/remotes/navigation/index.tsx';
import { AnalyticContextProvider } from '../../../common/controllers/analytic-context/index.tsx';
import { useCollectionSelectedMatch } from '../../../common/utils/router.tsx';

type RoadmapsProvidersProps = {
	children: React.ReactNode;
};

export const RoadmapsProviders = ({ children }: RoadmapsProvidersProps): React.JSX.Element => {
	const cloudId = useCloudId();
	const currentUser = useAccountId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<AnalyticContextProvider>
			<OnboardingFlowsContainer accountId={currentUser}>
				<NavigationCollectionsRemoteProvider>
					<NavigationViewRemoteProvider>
						<NavigationCollectionsContainerWithRemote cloudId={cloudId}>
							<EntityLimitsContainer isGlobal createAnalyticsEvent={createAnalyticsEvent}>
								<EmojiContainer cloudId={cloudId} currentUser={currentUser || undefined}>
									{children}
								</EmojiContainer>
							</EntityLimitsContainer>
						</NavigationCollectionsContainerWithRemote>
					</NavigationViewRemoteProvider>
				</NavigationCollectionsRemoteProvider>
			</OnboardingFlowsContainer>
		</AnalyticContextProvider>
	);
};

type NavigationCollectionsContainerWithRemoteProps = {
	cloudId: string;
	children: React.ReactNode;
};

const NavigationCollectionsContainerWithRemote = ({
	cloudId,
	children,
}: NavigationCollectionsContainerWithRemoteProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const remote = useNavigationCollectionsRemote();
	const collectionSelectedMatched = useCollectionSelectedMatch();

	const handleFailure: NavigationCollectionsOnFailure = useCallback((error, identifier) => {
		fireErrorAnalytics({
			error,
			meta: {
				id: 'jpdNavigationV4Action',
			},
			attributes: {
				action: identifier,
				type: 'collections',
			},
		});
	}, []);

	const handleSuccess = useCallback(
		(action: ActionWithSubject) => {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'jpdNavigationV4Action success', {
				action,
				type: 'collections',
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<NavigationCollectionsContainer
			isGlobal
			cloudId={cloudId}
			navigationCollectionsRemote={remote}
			activeCollectionUUID={collectionSelectedMatched?.collectionUUID}
			onFailure={handleFailure}
			onSuccess={handleSuccess}
		>
			{children}
		</NavigationCollectionsContainer>
	);
};
